import { usePage } from '@inertiajs/react'
import type { SharedProps, Translations } from '../types'

export function useShared(): SharedProps {
  return usePage<SharedProps>().props
}

/** Resolve a dotted key (e.g. "checkout.title") to a string or a subtree. */
function resolveNode(
  tree: Translations,
  key: string
): string | Translations | undefined {
  return key
    .split('.')
    .reduce<string | Translations | undefined>(
      (node, part) => (node && typeof node === 'object' ? node[part] : undefined),
      tree
    )
}

/**
 * Translation hook. Usage: const t = useT(); t('checkout.title')
 * Supports %{name} interpolation and count-based pluralization for keys shaped
 * like { one, other }: t('cart.item_count', { count: 3 }).
 */
export function useT() {
  const { translations } = useShared()
  return (key: string, vars?: Record<string, string | number>): string => {
    let node = resolveNode(translations, key)

    // Pluralization: pick the form when the key resolves to { one, other, ... }.
    if (node && typeof node === 'object') {
      const count = vars?.count
      const form = typeof count === 'number' && count === 1 ? 'one' : 'other'
      node = (node[form] ?? node.other ?? node.one) as string | undefined
    }

    let str = typeof node === 'string' ? node : key
    if (vars) {
      for (const [k, v] of Object.entries(vars)) {
        str = str.replace(new RegExp(`%\\{${k}\\}`, 'g'), String(v))
      }
    }
    return str
  }
}

export function useLocale() {
  const { locale, dir } = useShared()
  return { locale, dir, isRtl: dir === 'rtl' }
}
